import { Bookmark, Category } from '../types/bookmark'

export interface ImportResult {
  bookmarks: Omit<Bookmark, 'id'>[]
  categories: Omit<Category, 'id'>[]
  stats: {
    totalBookmarks: number
    totalCategories: number
    errors: string[]
  }
}

export class BookmarkImporter {
  private errors: string[] = []

  private parseJSON(content: string): ImportResult {
    try {
      const data = JSON.parse(content)
      if (!Array.isArray(data.bookmarks) || !Array.isArray(data.categories)) {
        throw new Error('无效的JSON格式')
      }

      return {
        bookmarks: data.bookmarks.map((b: any) => ({
          title: b.title,
          url: b.url,
          description: b.description,
          categoryId: b.categoryId,
          tags: Array.isArray(b.tags) ? b.tags : [],
          isFavorite: Boolean(b.isFavorite),
          createdAt: Number(b.createdAt) || Date.now(),
          updatedAt: Number(b.updatedAt) || Date.now(),
        })),
        categories: data.categories.map((c: any) => ({
          name: c.name,
          description: c.description,
          createdAt: Number(c.createdAt) || Date.now(),
          updatedAt: Number(c.updatedAt) || Date.now(),
        })),
        stats: {
          totalBookmarks: data.bookmarks.length,
          totalCategories: data.categories.length,
          errors: this.errors,
        },
      }
    } catch (error) {
      throw new Error('解析JSON文件失败：' + (error instanceof Error ? error.message : String(error)))
    }
  }

  private parseHTML(content: string): ImportResult {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, 'text/html')

      const categories = new Map<string, Omit<Category, 'id'>>()
      const bookmarks: Omit<Bookmark, 'id'>[] = []

      // 处理文件夹（DT/H3结构）
      const processNode = (node: Element, categoryName?: string) => {
        // 处理文件夹
        const folderTitle = node.querySelector('h3')?.textContent
        const currentCategory = folderTitle || categoryName

        if (currentCategory && !categories.has(currentCategory)) {
          categories.set(currentCategory, {
            name: currentCategory,
            description: `从浏览器导入的书签文件夹：${currentCategory}`,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        }

        // 处理书签链接
        const links = node.querySelectorAll('dt > a')
        links.forEach(link => {
          const addDate = parseInt(link.getAttribute('add_date') || '0') * 1000
          const lastModified = parseInt(link.getAttribute('last_modified') || '0') * 1000
          const tags = link.getAttribute('tags')?.split(',').map(tag => tag.trim()) || []

          bookmarks.push({
            title: link.textContent || '',
            url: link.getAttribute('href') || '',
            description: link.getAttribute('description') || undefined,
            categoryId: undefined, // 需要在导入时映射
            tags,
            isFavorite: false,
            createdAt: addDate || Date.now(),
            updatedAt: lastModified || Date.now(),
          })
        })

        // 递归处理子文件夹
        const subfolders = node.querySelectorAll(':scope > dl > dt > dl')
        subfolders.forEach(subfolder => {
          processNode(subfolder, currentCategory)
        })
      }

      const rootNode = doc.querySelector('dl')
      if (rootNode) {
        processNode(rootNode)
      }

      return {
        bookmarks,
        categories: Array.from(categories.values()),
        stats: {
          totalBookmarks: bookmarks.length,
          totalCategories: categories.size,
          errors: this.errors,
        },
      }
    } catch (error) {
      throw new Error('解析HTML文件失败：' + (error instanceof Error ? error.message : String(error)))
    }
  }

  private parseCSV(content: string): ImportResult {
    try {
      const lines = content.split('\n')
      if (lines.length < 2) {
        throw new Error('CSV文件格式不正确')
      }

      // 解析CSV行，处理引号和逗号
      const parseCSVLine = (line: string): string[] => {
        const fields: string[] = []
        let field = ''
        let inQuotes = false

        for (let i = 0; i < line.length; i++) {
          const char = line[i]
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              field += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (char === ',' && !inQuotes) {
            fields.push(field)
            field = ''
          } else {
            field += char
          }
        }
        fields.push(field)
        return fields
      }

      const headers = parseCSVLine(lines[0])
      const categories = new Map<string, Omit<Category, 'id'>>()
      const bookmarks: Omit<Bookmark, 'id'>[] = []

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue

        const fields = parseCSVLine(line)
        const record: { [key: string]: string } = {}
        headers.forEach((header, index) => {
          record[header] = fields[index] || ''
        })

        // 处理分类
        const categoryName = record['分类']?.trim()
        if (categoryName && !categories.has(categoryName)) {
          categories.set(categoryName, {
            name: categoryName,
            description: `从CSV导入的书签分类：${categoryName}`,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        }

        // 创建书签
        bookmarks.push({
          title: record['标题'] || '',
          url: record['URL'] || '',
          description: record['描述'] || undefined,
          categoryId: undefined, // 需要在导入时映射
          tags: record['标签']?.split(';').filter(Boolean) || [],
          isFavorite: record['收藏']?.includes('是') || false,
          createdAt: new Date(record['创建时间']).getTime() || Date.now(),
          updatedAt: new Date(record['更新时间']).getTime() || Date.now(),
        })
      }

      return {
        bookmarks,
        categories: Array.from(categories.values()),
        stats: {
          totalBookmarks: bookmarks.length,
          totalCategories: categories.size,
          errors: this.errors,
        },
      }
    } catch (error) {
      throw new Error('解析CSV文件失败：' + (error instanceof Error ? error.message : String(error)))
    }
  }

  import(content: string, format: 'json' | 'html' | 'csv'): ImportResult {
    this.errors = []

    switch (format) {
      case 'json':
        return this.parseJSON(content)
      case 'html':
        return this.parseHTML(content)
      case 'csv':
        return this.parseCSV(content)
      default:
        throw new Error('不支持的导入格式')
    }
  }
} 